import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFlag, FaTimes, FaPaperPlane, FaCheckCircle, FaEdit } from 'react-icons/fa';
import { Button } from './ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

/**
 * Composant de signalement d'erreur pour les quiz
 * Permet aux utilisateurs de signaler des problèmes ET de proposer la correction
 * L'IA apprend de ces signalements pour s'améliorer
 */
const ReportErrorButton = ({ 
  quizId, 
  questionId, 
  questionText, 
  options = [],
  correctIndex,
  onReported 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState('');
  const [errorType, setErrorType] = useState('outdated');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [proposedCorrection, setProposedCorrection] = useState('');

  const errorTypes = [
    { value: 'outdated', label: '📅 Information obsolète / pas à jour' },
    { value: 'wrong_answer', label: '❌ Mauvaise réponse marquée correcte' },
    { value: 'duplicate', label: '🔄 Réponses en double' },
    { value: 'unclear', label: '❓ Question peu claire' },
    { value: 'factual_error', label: '⚠️ Erreur factuelle' },
    { value: 'other', label: '📝 Autre problème' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await api.post('/report-error', {
        quizId,
        questionId,
        questionText,
        options,
        errorType,
        description,
        correctAnswer: correctAnswer || null,
        proposedCorrection: proposedCorrection || null,
        reportedAt: new Date().toISOString()
      });

      setSubmitted(true);
      
      // Afficher l'analyse de l'IA si disponible
      const aiCorrection = response.data.ai_correction;
      if (aiCorrection && aiCorrection.applied) {
        toast.success(`Merci ! L'IA a corrigé l'erreur (${Math.round(aiCorrection.confidence * 100)}% confiance)`);
      } else {
        toast.success('Erreur signalée ! Merci de votre contribution.');
      }
      
      // Notifier le parent que la question a été signalée
      if (onReported) {
        onReported(questionId);
      }

      setTimeout(() => {
        setShowModal(false);
        setSubmitted(false);
        setDescription('');
        setCorrectAnswer('');
        setProposedCorrection('');
      }, 2500);

    } catch (error) {
      console.error('Erreur signalement:', error);
      toast.error('Erreur lors du signalement. Réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-1 text-xs text-orange-600 hover:text-red-600 transition-colors font-medium"
        title="Signaler une erreur"
      >
        <FaFlag className="text-xs" />
        <span>Signaler une erreur</span>
      </button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              {submitted ? (
                <div className="text-center py-12 px-6">
                  <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Merci pour votre aide !</h3>
                  <p className="text-gray-600">
                    L'IA a pris en compte votre correction. Cette question ne comptera pas dans votre score.
                  </p>
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-bold text-gray-800">
                      <FaFlag className="inline mr-2 text-orange-500" />
                      Signaler une erreur
                    </h3>
                    <button
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <FaTimes />
                    </button>
                  </div>

                  {/* Question concernée */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="font-medium text-gray-700 text-sm mb-2">Question :</p>
                    <p className="text-gray-800 font-semibold">{questionText}</p>
                    {options.length > 0 && (
                      <div className="mt-2 text-sm text-gray-600">
                        <p className="font-medium mb-1">Options actuelles :</p>
                        {options.map((opt, i) => (
                          <div key={i} className={`${i === correctIndex ? 'text-green-600 font-medium' : ''}`}>
                            {String.fromCharCode(65 + i)}) {opt} {i === correctIndex && '✓'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Type d'erreur */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type de problème *
                      </label>
                      <select
                        value={errorType}
                        onChange={(e) => setErrorType(e.target.value)}
                        className="w-full border rounded-lg p-2.5 text-sm"
                      >
                        {errorTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Bonne réponse (si erreur factuelle ou outdated) */}
                    {(errorType === 'outdated' || errorType === 'wrong_answer' || errorType === 'factual_error') && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <label className="block text-sm font-bold text-blue-800 mb-2">
                          <FaEdit className="inline mr-1" />
                          Quelle est la BONNE réponse ?
                        </label>
                        <input
                          type="text"
                          value={correctAnswer}
                          onChange={(e) => setCorrectAnswer(e.target.value)}
                          placeholder="Ex: Garde des Sceaux, Ministre de la Justice"
                          className="w-full border border-blue-300 rounded-lg p-2.5 text-sm"
                        />
                        <p className="text-xs text-blue-600 mt-1">
                          Cette information sera utilisée pour corriger le quiz
                        </p>
                      </div>
                    )}

                    {/* Description du problème */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expliquez le problème *
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Ex: Gérald Darmanin n'est plus ministre de l'Intérieur depuis janvier 2025, il est maintenant Garde des Sceaux..."
                        className="w-full border rounded-lg p-2.5 h-24 text-sm"
                        required
                      />
                    </div>

                    {/* Info box */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800">
                      <strong>🤖 Autocorrection IA :</strong> Votre correction sera analysée par l'IA qui mettra à jour sa base de connaissances. Cette question ne comptera pas dans votre score.
                    </div>

                    {/* Boutons */}
                    <div className="flex gap-3 pt-2">
                      <Button
                        type="submit"
                        disabled={submitting || !description.trim()}
                        className="flex-1 bg-orange-600 hover:bg-orange-700"
                      >
                        {submitting ? (
                          'Envoi en cours...'
                        ) : (
                          <>
                            <FaPaperPlane className="mr-2" />
                            Envoyer la correction
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowModal(false)}
                      >
                        Annuler
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ReportErrorButton;
